import { Order } from '../../model/dto/order/order';
import { OrderChannel } from '../../model/type/order-channel.enum';
import DateUtils from './date-utils';

export class OrderDataUtils {
  static getItemCount(order: Pick<Order, 'items'>): number {
    return order.items.reduce(
      (partialSum, item) => partialSum + (item.quantity ?? 1),
      0
    );
  }

  static getDiscountTotal(order: Pick<Order, 'items'>): number {
    return order.items.reduce(
      (partialSum, item) =>
        partialSum + (item.discount ?? 0) * (item.quantity ?? 1),
      0
    );
  }

  static getCustomerShortName(order: Pick<Order, 'billing_address'>): string {
    const firstInitial = order.billing_address?.first_name?.substring(0, 1);

    return `${firstInitial || '-'}. ${order.billing_address?.last_name || '-'}`;
  }

  static formatAmount(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  static getItemsTotal(order: Pick<Order, 'items'>): number {
    return order.items.reduce(
      (partialSum, item) =>
        partialSum +
        ((item.price ?? 0) - (item.discount ?? 0)) * (item.quantity ?? 1),
      0
    );
  }

  static getTotal(
    order: Pick<Order, 'items' | 'shipping_rate_amount'>
  ): number {
    return (
      OrderDataUtils.getItemsTotal(order) + (order.shipping_rate_amount ?? 0)
    );
  }

  static formatDateTime(
    value?: string | Date | null,
    displayFormat: string = 'MMMM DD, YYYY, HH:mm:ss'
  ): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    return isNaN(date.getTime())
      ? '-'
      : DateUtils.formatDate(date, displayFormat);
  }

  static getStripePaymentUrl(
    order: Pick<Order, 'stripe_payment_intent_id'>
  ): string | undefined {
    return order.stripe_payment_intent_id
      ? `https://dashboard.stripe.com/payments/${order.stripe_payment_intent_id}`
      : undefined;
  }

  static getShopifyOrderUrl(
    order: Pick<Order, 'channel' | 'internal_id'>
  ): string | undefined {
    if (order.channel !== OrderChannel.Shopify) {
      return undefined;
    }

    const shopifyOrderId = order.internal_id?.match(/OOVA\d{8}(.*)$/)?.[1];

    return shopifyOrderId
      ? `https://oovahealth.myshopify.com/admin/orders/${shopifyOrderId}`
      : undefined;
  }
}
